import { AsYouType, CountryCode, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

const getCountryListElement = (el: HTMLElement) => {
  const listElement = el.querySelector('[data-component="country-list"]');

  if (!listElement) {
    throw new Error("listElement not found");
  }

  return listElement;
};

const getCurrentFlagContainerElement = (el: HTMLElement) => {
  const element = el.querySelector('[data-component="current-flag-container"]');

  if (!element) {
    throw new Error("Current flag container not found");
  }

  return element;
};

const getPhoneNumberInputElement = (el: HTMLElement): HTMLInputElement => {
  const inputElement = el.querySelector(
    '[data-component="international-phone-input"]',
  );
  if (!inputElement) {
    throw new Error("Input element not found");
  }

  return inputElement as HTMLInputElement;
};

const getCountryCodeInputElement = (el: HTMLElement): HTMLInputElement => {
  const inputElement = el.querySelector('[data-field="country-code"]');
  if (!inputElement) {
    throw new Error("Hidden form field element not found");
  }

  return inputElement as HTMLInputElement;
};

const updatePhoneNumberInputPlaceholder = (
  inputElement: HTMLInputElement,
  countryCode: string,
) => {
  const typedCountryCode = countryCode.toUpperCase() as CountryCode;
  const exampleNumber =
    getExampleNumber(typedCountryCode, examples)?.formatNational() ?? "";

  inputElement.placeholder = exampleNumber;

  return inputElement;
};

const updatePhoneNumberInput = (el: HTMLInputElement, newValue: string) => {
  el.value = newValue;
  const inputEvent = new CustomEvent("input", {
    bubbles: true,
    detail: {
      isFromInternationalPhoneNumber: true,
    },
  });
  el.dispatchEvent(inputEvent);
};

function isCustomEvent(event: Event): event is CustomEvent {
  return "detail" in event;
}

const InternationalPhoneNumber = {
  previousRawInput: null,
  clickAwayListener: null,
  mounted() {
    const countryListElement = getCountryListElement(this.el);
    const currentFlagContainerElement = getCurrentFlagContainerElement(this.el);
    const hiddenFormFieldElement = getCountryCodeInputElement(this.el);
    const inputElement = getPhoneNumberInputElement(this.el);

    updatePhoneNumberInputPlaceholder(
      getPhoneNumberInputElement(this.el),
      hiddenFormFieldElement.value,
    );

    inputElement.addEventListener("input", (event) => {
      if (isCustomEvent(event) && event.detail.isFromInternationalPhoneNumber) {
        return;
      }

      // Only stop AFTER this point. ORDER MATTERS;
      event.stopPropagation();

      const typedCountryCode =
        (inputElement?.dataset?.currentCountryCode?.toUpperCase() as CountryCode) ??
        "US";
      const asYouType = new AsYouType(typedCountryCode);
      const newValue = asYouType.input(
        (event?.target as HTMLInputElement)?.value,
      );
      const rawValue = asYouType.getChars();

      /**
       *  The intent for this check is to make sure the input doesn't get into a cycle
       *  when formatting values.
       *  For example, if the user types in the following, when the country is `US`
       *  208
       *  the output becomes after being passed to asYouType.input(...)
       *  (208)
       *  If the user backspaces, the value for the input event is
       *  (208
       *  which then goes through asYouType.input(...) and then becomes
       *  (208)
       *  This will essentially stop the user from being able to backspace any characters
       *  that aren't digits.
       */
      if (this.previousRawInput !== rawValue) {
        this.previousRawInput = rawValue;

        updatePhoneNumberInput(inputElement, newValue);
      }
    });

    // List click handler
    countryListElement.addEventListener("click", (event) => {
      const countryListItemElement = (event?.target as HTMLElement)?.closest(
        "li",
      );
      const hiddenFormFieldElement = getCountryCodeInputElement(this.el);

      if (countryListItemElement) {
        countryListElement.classList.add("hidden");
        const updatedCountryCode =
          countryListItemElement?.dataset?.countryCode ?? "US";

        updatePhoneNumberInputPlaceholder(
          getPhoneNumberInputElement(this.el),
          updatedCountryCode,
        );

        hiddenFormFieldElement.value = updatedCountryCode;
        hiddenFormFieldElement.dispatchEvent(
          new Event("input", { bubbles: true }),
        );
      }
    });

    currentFlagContainerElement.addEventListener("click", (_event: Event) => {
      countryListElement.classList.remove("hidden");
    });

    this.clickAwayListener = document.addEventListener("click", (event) => {
      if (
        !countryListElement.contains(event.target as HTMLElement) &&
        !currentFlagContainerElement.contains(event.target as HTMLElement)
      ) {
        countryListElement.classList.add("hidden");
      }
    });
  },

  updated() {
    updatePhoneNumberInputPlaceholder(
      getPhoneNumberInputElement(this.el),
      getCountryCodeInputElement(this.el).value,
    );

    const inputElement = getPhoneNumberInputElement(this.el);
    const typedCountryCode =
      (inputElement?.dataset?.currentCountryCode?.toUpperCase() as CountryCode) ??
      "US";
    const asYouType = new AsYouType(typedCountryCode);
    const newValue = asYouType.input(inputElement.value);
    inputElement.value = newValue;
    updatePhoneNumberInput(inputElement, newValue);
  },
  destroyed() {
    document.removeEventListener("click", this.clickAwayListener);
  },
};

export default InternationalPhoneNumber;
